import { InspectorIcon } from "../InspectorIcon";
import { useComposerMultilinePrompt } from "../chat/useComposerMultilinePrompt";
import { observeComposerOutsidePointer } from "../chat/composerOutsidePointer";
import {
  detectReviewMention,
  reviewMentionPreview,
  type ReviewMentionOption,
} from "./reviewMentions";
import { ReviewMentionPicker, type MentionKeyHandler } from "./ReviewMentionPicker";
import { replaceTextRange } from "@amoreg/shared/composerTrigger";
import { collapseExpandedComposerCursor } from "../../composer-logic";
import { useEffect, useRef, useState } from "react";
import {
  MessageSquareIcon,
  PaperclipIcon,
  XIcon,
  FileIcon,
  InfoIcon,
  CircleAlertIcon,
} from "lucide-react";
import { ComposerBannerStack } from "../chat/ComposerBannerStack";
import { reviewInlineFileAttachments, formatAttachmentSize } from "../chat/attachmentFiles";
import { ComposerPromptEditor, type ComposerPromptEditorHandle } from "../ComposerPromptEditor";
import {
  ComposerFrame,
  ComposerSurface,
  COMPOSER_BODY_CLASS_NAME,
  COMPOSER_RESTING_BODY_CLASS_NAME,
  COMPOSER_RESTING_EDITOR_CLASS_NAME,
  COMPOSER_RESTING_PLACEHOLDER_CLASS_NAME,
  COMPOSER_FOOTER_CLASS_NAME,
  COMPOSER_RESTING_FOOTER_CLASS_NAME,
  COMPOSER_LEFT_CONTROLS_CLASS_NAME,
} from "../chat/ComposerSurface";
import { cn } from "~/lib/utils";
import { Button } from "../ui/button";
import { TenantContextStrip } from "../BranchToolbar";
import { HoverCard, HoverCardPopup, HoverCardTrigger } from "../ui/hover-card";
import { Tabs, TabsList, TabsTab } from "../ui/tabs";
import { SEGMENTED_TAB_CLASS, SEGMENTED_TABS_LIST_CLASS } from "./PageHeader";

import { useMediaQuery } from "../../hooks/useMediaQuery";
import { useClientSettings } from "../../hooks/useSettings";
import { shouldUseRestingComposerLayout } from "../composerFooterLayout";
import {
  createComposerScrollGestureState,
  recordComposerScrollGestureEvent,
  resetComposerScrollGesture,
} from "../chat/composerScrollGesture";
import { useComposerRestingTransition } from "../chat/useComposerRestingTransition";
import { ComposerPrimaryActions } from "../chat/ComposerPrimaryActions";
import { ComposerControl, ComposerControlIcon } from "../chat/ComposerControl";
import type { ReviewRecipient } from "./reviewConversationView";

const noop = () => {};
const empty: [] = [];

export function ReviewPromptComposer({
  draft,
  onChange,
  recipient,
  onRecipientChange,
  canAskAI,
  posting,
  canSend,
  onSubmit,
  limit,
  error,
  inspectorStatus,
  onStop,
  stopping,
  files = [],
  onFilesChange,
  layout = "default",
  subject = "finding",
}: {
  draft: string;
  onChange: (text: string) => void;
  recipient: ReviewRecipient;
  onRecipientChange: (recipient: ReviewRecipient) => void;
  canAskAI: boolean;
  posting: boolean;
  canSend: boolean;
  onSubmit: () => void;
  limit: number;
  error: string | null;
  inspectorStatus?: string | null;
  onStop?: (() => void) | undefined;
  stopping?: boolean | undefined;
  files?: readonly File[];
  onFilesChange?: (files: File[]) => void;
  /**
   * "tabs": Inspector / Comment as tabs at the bottom left of the box in
   * place of the recipient button, and no context tray under the box. The
   * box rests as one line until it is focused or holds a draft or files.
   */
  layout?: "default" | "tabs";
  /** What Inspector is asked about, for the composer's wording. */
  subject?: "finding" | "suggestion";
}) {
  const tabbed = layout === "tabs";
  const editor = useRef<ComposerPromptEditorHandle>(null);
  const [cursor, setCursor] = useState(0);
  const [expandedCursor, setExpandedCursor] = useState(0);
  const [dismissedMention, setDismissedMention] = useState<string | null>(null);
  const mentionKeyHandler = useRef<MentionKeyHandler | null>(null);
  const mention = detectReviewMention(draft, expandedCursor);
  const mentionKey = JSON.stringify([draft, expandedCursor]);
  const insertMention = (option: ReviewMentionOption) => {
    if (!mention) return;
    const next = replaceTextRange(draft, mention.rangeStart, mention.rangeEnd, `${option.text} `);
    const nextCursor = collapseExpandedComposerCursor(next.text, next.cursor);
    setCursor(nextCursor);
    setExpandedCursor(next.cursor);
    onChange(next.text);
    requestAnimationFrame(() => editor.current?.focusAt(nextCursor));
  };
  const [focused, setFocused] = useState(false);
  const restingControls = useRef<HTMLDivElement>(null);
  const form = useRef<HTMLFormElement>(null);
  const outsidePointerPending = useRef(false);
  useEffect(() => {
    if (!focused) return;
    return observeComposerOutsidePointer({
      target: document,
      contains: (target) => target instanceof Node && Boolean(form.current?.contains(target)),
      onPendingChange: (pending) => {
        outsidePointerPending.current = pending;
      },
      onSettled: () => {
        if (!form.current?.contains(document.activeElement)) setFocused(false);
      },
    });
  }, [focused]);
  const isMobile = useMediaQuery("max-sm");
  const collapseOnScroll = useClientSettings((settings) => settings.composerCollapseOnScroll);
  const [scrollCollapsed, setScrollCollapsed] = useState(false);
  const [timelineOverflows, setTimelineOverflows] = useState(true);
  useEffect(() => {
    const scroll = form.current
      ?.closest("[data-regulation-page]")
      ?.querySelector<HTMLElement>(".regulation-page-scroll-fade");
    const content = scroll?.firstElementChild;
    if (!scroll || !content) return;
    const measure = () => setTimelineOverflows(scroll.scrollHeight > scroll.clientHeight + 1);
    // Observe page content, not the changing composer inset, so collapsing
    // near the overflow threshold cannot make the two layouts oscillate.
    const observer = new ResizeObserver(measure);
    observer.observe(content);
    window.addEventListener("resize", measure);
    measure();
    return () => {
      observer.disconnect();
      window.removeEventListener("resize", measure);
    };
  }, []);
  useEffect(() => {
    if (!collapseOnScroll || isMobile) return;
    const scroll = form.current
      ?.closest("[data-regulation-page]")
      ?.querySelector<HTMLElement>(".regulation-page-scroll-fade");
    if (!scroll) return;
    const gesture = createComposerScrollGestureState();
    let timeout: ReturnType<typeof setTimeout> | undefined;
    const wheel = (event: WheelEvent) => {
      if (event.ctrlKey || !(event.target instanceof Node) || !scroll.contains(event.target))
        return;
      clearTimeout(timeout);
      timeout = setTimeout(() => resetComposerScrollGesture(gesture), 120);
      const atEnd = scroll.scrollTop >= scroll.scrollHeight - scroll.clientHeight - 1;
      if (
        recordComposerScrollGestureEvent(gesture, {
          now: performance.now(),
          deltaPx:
            Math.abs(event.deltaY) *
            (event.deltaMode === 1 ? 16 : event.deltaMode === 2 ? scroll.clientHeight : 1),
          collapseThresholdPx: 24,
          collapseEligible: true,
          canScrollInGestureDirection: event.deltaY < 0 ? scroll.scrollTop > 0 : !atEnd,
          scrollsTowardLogicalEnd: event.deltaY > 0 && atEnd,
        })
      )
        setScrollCollapsed(true);
    };
    scroll.addEventListener("wheel", wheel, { passive: true });
    return () => {
      scroll.removeEventListener("wheel", wheel);
      clearTimeout(timeout);
    };
  }, [collapseOnScroll, isMobile]);
  const [body, setBody] = useState<HTMLDivElement | null>(null);
  const hasWrappedPrompt = useComposerMultilinePrompt(body, draft);
  const hasMultilinePrompt =
    draft.includes("\n") || (draft.length > 0 && hasWrappedPrompt !== false);
  const mobileCollapsed = !tabbed && isMobile && !focused && !hasMultilinePrompt;
  const collapsed = tabbed
    ? !focused && !draft.trim() && files.length === 0
    : mobileCollapsed ||
      shouldUseRestingComposerLayout({
        isExistingThread: true,
        isMobileViewport: isMobile,
        isScrollCollapsed: scrollCollapsed,
        hasExpandedChrome: false,
        hasMultilinePrompt,
        timelineOverflows,
      });
  const surface = useComposerRestingTransition(collapsed, collapsed, restingControls, noop);
  const fileInput = useRef<HTMLInputElement>(null);
  const [fileError, setFileError] = useState<string | null>(null);
  const addFiles = (incoming: readonly File[]) => {
    if (posting) {
      setFileError("Wait for this message to finish sending before attaching files.");
      return;
    }
    if (!canAskAI || !onFilesChange) {
      setFileError("File attachments require an available Inspector review.");
      return;
    }
    const result = reviewInlineFileAttachments({ files: incoming, reservedCount: files.length });
    onFilesChange([...files, ...result.accepted]);
    setFileError(result.error);
    if (result.accepted.length) {
      setScrollCollapsed(false);
      setFocused(true);
      requestAnimationFrame(() => editor.current?.focusAtEnd());
    }
  };
  const inspector = recipient === "inspector";
  const status = inspector ? inspectorStatus : null;
  const overLimit = draft.trim().length > limit;
  const message =
    error ??
    fileError ??
    (!inspector && files.length
      ? "Switch to Inspector to send these files, or remove them to post a comment."
      : null) ??
    (overLimit
      ? `Shorten your ${inspector ? "request" : "comment"} to ${limit.toLocaleString()} characters.`
      : (status ??
        (inspector && !canAskAI
          ? `Reopen this ${subject} to request changes, or switch to Comment.`
          : null)));
  const messageVariant =
    error || fileError
      ? "error"
      : overLimit || (!inspector && files.length > 0) || (inspector && !canAskAI)
        ? "warning"
        : "info";
  const recipientExplanation = canAskAI
    ? `Inspector asks AI to review this ${subject} and propose changes. Comment posts a message for your team without starting Inspector. Click to switch; your draft stays here.`
    : "Inspector is unavailable for this review. You can still switch to Comment and leave a message for your team.";
  const recipientControl = (
    <HoverCard>
      <HoverCardTrigger
        render={
          <ComposerControl
            type="button"
            size={collapsed ? "xs" : "sm"}
            className={collapsed ? "text-xs" : undefined}
            disabled={posting}
            aria-disabled={posting || (!inspector && !canAskAI)}
            aria-label={`Message recipient: ${inspector ? "Inspector" : "Comment"}`}
            aria-description={recipientExplanation}
            data-review-recipient-toggle="true"
            onPointerDown={(event) => event.preventDefault()}
            onClick={() => {
              if (!posting && (inspector || canAskAI))
                onRecipientChange(inspector ? "comment" : "inspector");
            }}
          />
        }
      >
        <ComposerControlIcon
          size={collapsed ? "xs" : "sm"}
          icon={inspector ? InspectorIcon : MessageSquareIcon}
        />
        {inspector ? "Inspector" : "Comment"}
      </HoverCardTrigger>
      <HoverCardPopup side="top" align="start" className="w-72 p-3 text-xs leading-relaxed">
        <p className="mb-1 font-medium text-foreground">Inspector or team comment</p>
        <p className="text-muted-foreground">{recipientExplanation}</p>
      </HoverCardPopup>
    </HoverCard>
  );
  const primaryActions = (
    <ComposerPrimaryActions
      compact
      preserveComposerFocusOnPointerDown={collapsed || isMobile}
      pendingAction={null}
      isRunning={Boolean(inspector && onStop && !stopping)}
      sendLabel={
        inspector && stopping ? "Stopping Inspector" : inspector ? "Ask Inspector" : "Post comment"
      }
      stopLabel="Stop Inspector"
      isSendBusy={posting || (inspector && Boolean(stopping))}
      sendDisabledReason={canSend ? null : "Cannot send"}
      isConnecting={false}
      isEnvironmentUnavailable={false}
      isPreparingWorktree={false}
      hasSendableContent={canSend}
      onPreviousPendingQuestion={noop}
      onInterrupt={onStop ?? noop}
    />
  );
  const attachControl =
    onFilesChange && canAskAI ? (
      <>
        <input
          ref={fileInput}
          type="file"
          multiple
          className="hidden"
          aria-label="Attach files to Inspector"
          disabled={posting || !inspector}
          onChange={(event) => {
            addFiles(Array.from(event.currentTarget.files ?? []));
            event.currentTarget.value = "";
          }}
        />
        <Button
          type="button"
          variant="ghost"
          size="icon-sm"
          onPointerDown={(event) => event.preventDefault()}
          aria-label="Attach files"
          title="Attach any file · up to 10 MB each"
          disabled={posting || !inspector}
          onClick={() => fileInput.current?.click()}
        >
          <PaperclipIcon />
        </Button>
      </>
    ) : null;
  const counter =
    draft.length > limit - 200 ? (
      <span className="text-xs tabular-nums text-muted-foreground">
        {draft.trim().length} / {limit}
      </span>
    ) : null;
  const recipientTabs = (
    <Tabs
      value={recipient}
      onValueChange={(value) => {
        if (posting || value === recipient) return;
        if (value === "inspector" && !canAskAI) return;
        onRecipientChange(value as ReviewRecipient);
      }}
    >
      <TabsList
        aria-label="Send to"
        className={SEGMENTED_TABS_LIST_CLASS}
        data-review-recipient-tabs="true"
      >
        {(["inspector", "comment"] as const).map((value) => (
          <TabsTab
            key={value}
            value={value}
            disabled={posting || (value === "inspector" && !canAskAI)}
            onPointerDown={(event) => event.preventDefault()}
            className={SEGMENTED_TAB_CLASS}
          >
            {value === "inspector" ? (
              <InspectorIcon ink="inherit" className="size-3.5" />
            ) : (
              <MessageSquareIcon className="size-3.5" />
            )}
            {value === "inspector" ? "Inspector" : "Comment"}
          </TabsTab>
        ))}
      </TabsList>
    </Tabs>
  );
  return (
    <form
      ref={form}
      data-chat-composer-form="true"
      data-review-composer-resting={collapsed ? "true" : "false"}
      aria-label={inspector ? "Inspector request composer" : "Team comment composer"}
      className="relative w-full min-w-0"
      onKeyDownCapture={(event) => {
        if (event.key === "Escape") mentionKeyHandler.current?.(event.key, event.nativeEvent);
      }}
      onPointerDownCapture={(event) => {
        if (
          !(event.target instanceof Element) ||
          event.target.closest("button, input, [data-chat-composer-resting-controls]")
        )
          return;
        setScrollCollapsed(false);
        if (collapsed && !event.target.closest('[data-testid="composer-editor"]')) {
          event.preventDefault();
          setFocused(true);
          requestAnimationFrame(() => editor.current?.focusAtEnd());
        }
      }}
      onFocusCapture={(event) => {
        if (!event.currentTarget.contains(event.target)) return;
        if (
          (event.target as Element).closest(
            "[data-chat-composer-resting-controls], [data-review-recipient-toggle]",
          )
        )
          return;
        setScrollCollapsed(false);
        setFocused(true);
      }}
      onBlurCapture={(event) => {
        if (
          !outsidePointerPending.current &&
          !event.currentTarget.contains(event.relatedTarget as Node | null)
        )
          setFocused(false);
      }}
      onDragOver={(event) => {
        if (event.dataTransfer.types.includes("Files")) event.preventDefault();
      }}
      onDrop={(event) => {
        if (!event.dataTransfer.files.length) return;
        event.preventDefault();
        addFiles(Array.from(event.dataTransfer.files));
      }}
      onSubmit={(event) => {
        event.preventDefault();
        onSubmit();
      }}
    >
      {focused && !posting && mention && dismissedMention !== mentionKey ? (
        <ReviewMentionPicker
          kind={mention.kind}
          query={mention.query}
          onSelect={insertMention}
          onDismiss={() => setDismissedMention(mentionKey)}
          keyHandler={mentionKeyHandler}
        />
      ) : null}
      {message ? (
        <ComposerBannerStack
          className="max-w-none"
          items={[
            {
              id: "review-composer-message",
              variant: messageVariant,
              icon:
                messageVariant === "info" ? (
                  <InfoIcon aria-hidden />
                ) : (
                  <CircleAlertIcon aria-hidden />
                ),
              title: message,
            },
          ]}
        />
      ) : null}
      <div
        className={cn(
          "chat-composer-glass-shell",
          !tabbed && "chat-composer-glass-shell-with-context",
          message && "chat-composer-glass-shell-attached",
        )}
      >
        <div className="chat-composer-glass-host relative z-10 rounded-[16px]">
          <ComposerFrame ref={surface}>
            <ComposerSurface
              data-chat-composer-mobile-collapsed={mobileCollapsed ? "true" : "false"}
            >
              {mobileCollapsed ? (
                <div className="flex items-center justify-between gap-2 px-3 py-2">
                  <button
                    type="button"
                    data-chat-composer-transition-prompt="true"
                    className={cn(
                      "min-w-0 flex-1 truncate bg-transparent p-0 text-left text-[14px] focus:outline-none",
                      draft.trim() ? "text-foreground" : "text-placeholder",
                    )}
                    onPointerDown={(event) => event.preventDefault()}
                    onClick={() => {
                      setFocused(true);
                      requestAnimationFrame(() => editor.current?.focusAtEnd());
                    }}
                    aria-label="Expand composer"
                  >
                    {reviewMentionPreview(draft.trim()) ||
                      (inspector
                        ? `Ask Inspector to review or change this ${subject}…`
                        : "Write a comment for your team…")}
                  </button>
                  <div data-chat-composer-transition-actions="true" className="[&_button]:size-8">
                    {primaryActions}
                  </div>
                </div>
              ) : null}
              <div
                ref={setBody}
                data-chat-composer-body="true"
                className={cn(
                  COMPOSER_BODY_CLASS_NAME,
                  collapsed && COMPOSER_RESTING_BODY_CLASS_NAME,
                  mobileCollapsed && "hidden",
                )}
              >
                {files.length ? (
                  <ul
                    aria-label="Attached files"
                    className="mb-3 flex max-h-24 flex-wrap gap-2 overflow-y-auto"
                  >
                    {files.map((file, index) => (
                      <li
                        key={`${index}:${file.name}`}
                        className="flex min-w-0 max-w-full items-center gap-2 rounded-lg border border-border/60 bg-background/40 px-2 py-1.5 text-xs"
                      >
                        <FileIcon className="size-4 shrink-0 text-muted-foreground" />
                        <span className="truncate" title={file.name}>
                          {file.name}
                        </span>
                        <span className="shrink-0 text-muted-foreground">
                          {formatAttachmentSize(file.size)}
                        </span>
                        <button
                          type="button"
                          disabled={posting}
                          aria-label={`Remove ${file.name}`}
                          className="shrink-0 rounded p-1 hover:bg-muted focus-visible:ring-2 focus-visible:ring-ring"
                          onClick={() => {
                            onFilesChange?.(files.filter((_, i) => i !== index));
                            setFileError(null);
                          }}
                        >
                          <XIcon className="size-3.5" />
                        </button>
                      </li>
                    ))}
                  </ul>
                ) : null}
                <div
                  className={cn(
                    "relative",
                    collapsed && "flex min-w-0 items-center gap-1",
                    collapsed && (onFilesChange && canAskAI ? "pr-20" : "pr-12"),
                  )}
                >
                  <ComposerPromptEditor
                    containerClassName={cn(collapsed && "min-w-0 flex-1")}
                    className={cn(collapsed && COMPOSER_RESTING_EDITOR_CLASS_NAME)}
                    placeholderClassName={cn(collapsed && COMPOSER_RESTING_PLACEHOLDER_CLASS_NAME)}
                    editorRef={editor}
                    value={draft}
                    cursor={cursor}
                    terminalContexts={empty}
                    skills={empty}
                    disabled={posting}
                    ariaLabel={
                      recipient === "inspector" ? "Write an Inspector request" : "Write a comment"
                    }
                    placeholder={
                      inspector
                        ? `Ask Inspector to review or change this ${subject}…`
                        : "Write a comment for your team…"
                    }
                    onRemoveTerminalContext={noop}
                    onPaste={(event) => {
                      if (!event.clipboardData.files.length) return;
                      event.preventDefault();
                      addFiles(Array.from(event.clipboardData.files));
                    }}
                    onChange={(text, nextCursor, nextExpandedCursor) => {
                      setExpandedCursor(nextExpandedCursor);
                      setScrollCollapsed(false);
                      setFocused(true);
                      setCursor(nextCursor);
                      onChange(text);
                    }}
                    onCommandKeyDown={(key, event) => {
                      if (mentionKeyHandler.current?.(key, event)) return true;
                      if (key !== "Enter" || event.shiftKey || event.isComposing) return false;
                      event.preventDefault();
                      onSubmit();
                      return true;
                    }}
                  />
                </div>
              </div>
              <div
                data-chat-composer-footer="true"
                className={cn(
                  COMPOSER_FOOTER_CLASS_NAME,
                  collapsed && COMPOSER_RESTING_FOOTER_CLASS_NAME,
                  mobileCollapsed && "hidden",
                )}
              >
                <div
                  data-chat-composer-controls="left"
                  className={cn(COMPOSER_LEFT_CONTROLS_CLASS_NAME, collapsed && "hidden")}
                >
                  {tabbed ? recipientTabs : !collapsed ? recipientControl : null}
                </div>
                <div
                  data-chat-composer-transition-actions="true"
                  className="flex shrink-0 flex-nowrap items-center justify-end gap-2"
                >
                  {attachControl}
                  {counter}
                  {primaryActions}
                </div>
              </div>
            </ComposerSurface>
          </ComposerFrame>
        </div>
        {tabbed ? (
          <div ref={restingControls} data-chat-composer-resting-controls="true" hidden />
        ) : (
          <TenantContextStrip className="min-h-13 max-w-none sm:min-h-12">
            <div
              ref={restingControls}
              data-chat-composer-resting-controls="true"
              className="flex h-7 min-w-0 items-center text-xs sm:h-6"
            >
              {collapsed ? recipientControl : null}
            </div>
          </TenantContextStrip>
        )}
      </div>
    </form>
  );
}
